import { Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { message as antdMessage } from 'antd';
import { OpenAPI } from '@/apis';
import { uploadFile } from '@/utils/upload';

/** 用于预览的 URL：完整 URL 在开发环境下改为同源路径走代理，避免裂图 */
function getPreviewUrl(url: string): string {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    if (import.meta.env.DEV && (url.includes('localhost:3030') || url.includes('127.0.0.1:3030'))) {
      try {
        const u = new URL(url);
        return u.pathname + u.search;
      } catch {
        return url;
      }
    }
    return url;
  }
  const base = OpenAPI.BASE || '';
  return url.startsWith('/') ? `${base}${url}` : `${base}/${url}`;
}

export type ProductSpec = { label: string; value: string };

export type ProductRow = Record<string, unknown> & {
  id: number;
  name?: string;
  category?: string;
  summary?: string;
  description?: string;
  content?: string;
  price?: string;
  image?: string | string[];
  images?: string[];
  specs?: ProductSpec[] | Array<string>;
  isFeatured?: number;
  status?: string;
};

export function toStrList(v: string | string[] | undefined): string[] {
  if (v == null) return [];
  return Array.isArray(v) ? v : [v].filter(Boolean);
}

export function toSpecList(v: ProductSpec[] | Array<string> | undefined): ProductSpec[] {
  if (v == null || !Array.isArray(v)) return [];
  return v.map((item) => {
    if (typeof item === 'object' && item !== null && 'label' in item && 'value' in item) {
      return { label: (item as ProductSpec).label ?? '', value: (item as ProductSpec).value ?? '' };
    }
    return { label: String(item), value: '' };
  });
}

export function normalizeRows(rows: unknown[]): ProductRow[] {
  return (rows || []).map((r: unknown) => {
    const item = r as Record<string, unknown>;
    const data = (item.data as Record<string, unknown>) || item;
    return { ...item, ...data, id: (data.id as number) ?? (item.id as number) } as ProductRow;
  });
}

/** 接口返回的 data 可能是 string[] 或 { url: string; name?: string }[] */
function parseUploadResponseData(d: unknown): string[] {
  if (!d || !Array.isArray(d)) return [];
  return d.map((item) => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object' && 'url' in item && typeof (item as { url: string }).url === 'string') {
      return (item as { url: string }).url;
    }
    return '';
  }).filter(Boolean);
}

export { uploadFile } from '@/utils/upload';

function loadImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('无法读取图片尺寸'));
    };
    img.src = url;
  });
}

export type ImageRules = {
  /** 最小宽度（像素） */
  minWidth?: number;
  /** 最小高度（像素） */
  minHeight?: number;
  /** 宽高比（宽/高），如 1 表示 1:1，允许误差约 5% */
  aspectRatio?: number;
};

export function MultiImageUpload({
  value,
  onChange,
  maxCount = 9,
  imageRules,
}: {
  value?: string[];
  onChange?: (urls: string[]) => void;
  maxCount?: number;
  imageRules?: ImageRules;
}) {
  const urlStrings = (value ?? []).map((item) =>
    typeof item === 'string' ? item : (item && typeof item === 'object' && 'url' in item ? (item as { url: string }).url : '')
  ).filter(Boolean) as string[];
  const list: UploadFile[] = urlStrings.map((url, i) => ({
    uid: `-${i}-${url}`,
    name: typeof url === 'string' ? (url.slice(url.lastIndexOf('/') + 1) || 'image') : 'image',
    status: 'done',
    url: getPreviewUrl(url),
  }));

  const handleChange: UploadProps['onChange'] = (info) => {
    const urls = info.fileList.flatMap((f) => {
      if (f.status !== 'done') return [];
      if (f.url) return [f.url];
      const r = f.response;
      if (Array.isArray(r)) return parseUploadResponseData(r);
      return [];
    });
    onChange?.(urls.slice(0, maxCount));
  };

  const customRequest: UploadProps['customRequest'] = async (opt) => {
    const file = opt.file as File;
    if (imageRules && /^image\//.test(file.type)) {
      try {
        const { width, height } = await loadImageDimensions(file);
        if (imageRules.minWidth != null && width < imageRules.minWidth) {
          antdMessage.error(`主图宽度不得低于 ${imageRules.minWidth} 像素，当前 ${width}px`);
          opt.onError?.(new Error('尺寸不符'));
          return;
        }
        if (imageRules.minHeight != null && height < imageRules.minHeight) {
          antdMessage.error(`主图高度不得低于 ${imageRules.minHeight} 像素，当前 ${height}px`);
          opt.onError?.(new Error('尺寸不符'));
          return;
        }
        if (imageRules.aspectRatio != null) {
          const ratio = width / height;
          const target = imageRules.aspectRatio;
          const tolerance = 0.05;
          if (ratio < target - tolerance || ratio > target + tolerance) {
            antdMessage.error(`主图请使用 1:1 比例，当前 ${width}×${height}`);
            opt.onError?.(new Error('宽高比不符'));
            return;
          }
        }
      } catch (e) {
        antdMessage.error('无法读取图片信息');
        opt.onError?.(e as Error);
        return;
      }
    }
    try {
      const urls = await uploadFile(file);
      const next = [...(value ?? []), ...urls].slice(0, maxCount);
      onChange?.(next);
      opt.onSuccess?.(urls);
    } catch (e) {
      antdMessage.error('上传失败');
      opt.onError?.(e as Error);
    }
  };

  return (
    <Upload
      listType="picture-card"
      fileList={list}
      onChange={handleChange}
      customRequest={customRequest}
      accept="image/*"
      maxCount={maxCount}
    >
      {list.length >= maxCount ? null : (
        <div>
          <UploadOutlined />
          <div style={{ marginTop: 8 }}>上传</div>
        </div>
      )}
    </Upload>
  );
}
